import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { RootState } from "../../store/store";

export interface PaymentGateway {
  paymentGatewayId: number;
  name: string;
  keysSchema: Record<string, any>;
  createdAt: string;
  updatedAt: string;
}

interface PaymentGatewayState {
  loading: boolean;
  error: string | null;
  success: string | null;
  paymentGateways: PaymentGateway[] | null;
  selectedGatewaySchema: Record<string, any> | null;
  selectedGatewayData: {
    paymentGatewayId: number;
    name: string;
    keysSchema: Record<string, any>;
    existingKeys: Record<string, any> | null;
  } | null;
}

const initialState: PaymentGatewayState = {
  loading: false,
  error: null,
  success: null,
  paymentGateways: null,
  selectedGatewaySchema: null,
  selectedGatewayData: null,
};

export const fetchPaymentGateways = createAsyncThunk(
  "paymentGateway/fetchPaymentGateways",
  async (distributorId: number, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("accessToken");
      const url = `${window.env.API_URL}/api/v1/payment/get-payment-gateways`;
      console.log('DEBUG: Payment Gateway API URL:', url);
      console.log('DEBUG: window.env.API_URL:', window.env.API_URL);
      
      const response = await axios.request({
        url: url,
        timeout: 10000,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "distributorid": distributorId.toString()
        },
        method: "GET",
      });
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.message || "Failed to fetch payment gateways");
    }
  }
);

export const fetchPaymentGatewayKeysSchema = createAsyncThunk(
  "paymentGateway/fetchPaymentGatewayKeysSchema",
  async ({ paymentGatewayId, distributorId }: { paymentGatewayId: number; distributorId: number }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("accessToken");
      const url = `${window.env.API_URL}/api/v1/payment/get-keys-schema/${paymentGatewayId}`;
      console.log('DEBUG: Payment Gateway Keys Schema API URL:', url);
      
      const response = await axios.request({
        url: url,
        timeout: 10000,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "distributorid": distributorId.toString()
        },
        method: "GET",
      });
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.message || "Failed to fetch payment gateway keys schema");
    }
  }
);

export const updatePaymentGatewayConfig = createAsyncThunk(
  "payment/updatePaymentGatewayConfig",
  async ({ paymentGatewayId, config, distributorId }: { paymentGatewayId: number; config: Record<string, any>; distributorId: number }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("accessToken");
      
      const response = await axios.post(
        `${window.env.API_URL}/api/v1/payment/set-keys/${paymentGatewayId}`,
        config,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'distributorid': distributorId.toString()
          }
        }
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.message || "Failed to update payment gateway configuration");
    }
  }
);

const paymentGatewaySlice = createSlice({
  name: "paymentGateway",
  initialState,
  reducers: {
    clearPaymentGateways(state) {
      state.paymentGateways = null;
      state.loading = false;
      state.error = null;
      state.success = null;
    },
    clearSelectedGatewaySchema(state) {
      state.selectedGatewaySchema = null;
      state.selectedGatewayData = null;
    },
    clearMessages(state) {
      state.error = null;
      state.success = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPaymentGateways.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(fetchPaymentGateways.fulfilled, (state, action: PayloadAction<PaymentGateway[]>) => {
        state.loading = false;
        state.paymentGateways = action.payload;
      })
      .addCase(fetchPaymentGateways.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || "Failed to fetch payment gateways";
      })
      .addCase(fetchPaymentGatewayKeysSchema.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(fetchPaymentGatewayKeysSchema.fulfilled, (state, action: PayloadAction<{
        paymentGatewayId: number;
        name: string;
        keysSchema: Record<string, any>;
        existingKeys: Record<string, any> | null;
      }>) => {
        state.loading = false;
        state.selectedGatewaySchema = action.payload.keysSchema;
        state.selectedGatewayData = action.payload;
      })
      .addCase(fetchPaymentGatewayKeysSchema.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || "Failed to fetch payment gateway keys schema";
      })
      .addCase(updatePaymentGatewayConfig.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(updatePaymentGatewayConfig.fulfilled, (state, action) => {
        state.loading = false;
        state.success = action.payload.message || "Payment gateway configuration updated successfully";
      })
      .addCase(updatePaymentGatewayConfig.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || "Failed to update payment gateway configuration";
      });
  },
});

export const { clearPaymentGateways, clearSelectedGatewaySchema, clearMessages } = paymentGatewaySlice.actions;
export default paymentGatewaySlice.reducer; 